import { captureMediaLoadError } from 'common/modules/media/captureMediaLoadError';
import React, { useCallback, useEffect, useState } from 'react';
import { IconPlaceholder } from 'students/views/shared/components/AnimatedIcon/IconPlaceholder';
import styled from 'styled-components';

interface IProps {
  className?: string;
  src: string | null;
  srcSet?: string;
  alt?: string;
  lazy?: boolean;
  fallbackImage?: string | null;
  onError?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  title?: string;
}

const Picture: React.FC<IProps> = ({
  className,
  src,
  srcSet,
  alt,
  onError,
  fallbackImage,
  title,
  lazy = false
}) => {
  const [imageSrc, setImageSrc] = useState(src || fallbackImage);
  const [exhausted, setExhausted] = useState(false);

  useEffect(() => {
    setExhausted(false);
    if (!src) {
      fallbackImage && setImageSrc(fallbackImage);
    } else {
      setImageSrc(src);
    }
  }, [src, fallbackImage]);

  const handleError = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement, Event>): void => {
      // Report the primary source failing; dedup lives in captureMediaLoadError.
      // Skip when there's no primary src — an error then means a broken fallback
      // image, not a content failure.
      if (src) {
        captureMediaLoadError(new Error('Image failed to load'), {
          kind: 'image',
          src,
          context: 'picture'
        });
      }
      onError && onError(event);

      if (fallbackImage && imageSrc !== fallbackImage) {
        setImageSrc(fallbackImage);
        return;
      }
      // Nothing left to try: stop rendering <img> so the broken-image glyph disappears.
      setExhausted(true);
    },
    [fallbackImage, imageSrc, onError, src]
  );

  // A provided-but-failed source must degrade visibly: call sites drop <Picture> into
  // fixed-size containers. A src never provided stays a legit empty state (`!imageSrc`).
  if (exhausted) {
    return <IconPlaceholder className={className} width="100%" height="100%" />;
  }

  if (!imageSrc) return null;

  return (
    <picture>
      {srcSet && <source srcSet={srcSet} type="image/webp" />}
      <SImg
        loading={lazy ? 'lazy' : 'eager'}
        src={imageSrc}
        alt={alt}
        onError={handleError}
        title={title}
        className={className}
      />
    </picture>
  );
};

export default Picture;

const SImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
